<template>
  <div class="field clinic-multi-field">
    <label class="field-lbl">
      {{ label }}
      <span v-if="value.length" class="clinic-multi-count">
        · รวม {{ coveredKeys.size }} รายการ ·
        <button type="button" class="clinic-multi-clear" data-testid="item-filter-clear" @click="clear">
          ล้าง
        </button>
      </span>
    </label>
    <multiselect
      class="multiselect-h-36px clinic-multi"
      data-testid="item-filter"
      :value="selectedOptions"
      :options="options"
      :multiple="true"
      :close-on-select="false"
      :taggable="true"
      :custom-label="optionLabel"
      :options-limit="300"
      track-by="_track"
      :placeholder="value.length ? 'เพิ่มรายการ…' : placeholder"
      tagPlaceholder=""
      selectLabel=""
      selectedLabel=""
      deselectLabel=""
      @input="onInput"
      @tag="addKeyword"
    >
      <template #option="{ option }">
        <span v-if="option.isTag" class="clinic-multi-keyword-opt">
          <template v-if="keywordCount(option.label)">
            <i class="fas fa-layer-group mr-1"></i>
            เลือกทุกรายการที่มีคำว่า "{{ option.label }}" ({{ keywordCount(option.label) }} รายการ)
          </template>
          <template v-else>ไม่พบรายการที่มีคำว่า "{{ option.label }}"</template>
        </span>
        <span v-else>
          {{ optionLabel(option) }}
          <span v-if="option.isDrug" class="clinic-multi-drug-badge">ยา/สินค้า</span>
        </span>
      </template>
      <template #tag="{ option, remove }">
        <span
          class="multiselect__tag"
          :class="{
            'clinic-multi-tag-keyword': option.type === 'keyword',
            'clinic-multi-tag-drug': option.isDrug,
          }"
        >
          <span>{{ optionLabel(option) }}</span>
          <i
            tabindex="1"
            class="multiselect__tag-icon"
            @keypress.enter.prevent="remove(option)"
            @mousedown.prevent="remove(option)"
          ></i>
        </span>
      </template>
      <span slot="noResult">ไม่พบรายการ กรุณาเปลี่ยนคำค้นหา</span>
      <span slot="noOptions">ยังไม่มีรายการให้เลือก กด "แสดงข้อมูล" ก่อน</span>
    </multiselect>
  </div>
</template>

<script>
import { itemLabel, keywordMatches, selectedItemKeys } from "@/utils/clinicReportFilters";

// Multi-item picker for the clinic-level reports (treatments + drugs/products, lab items).
// v-model is an array of { type: "item", key, label } | { type: "keyword", keyword };
// an empty array means every item. Typing a word or code prefix (e.g. "จัดฟัน", "TD")
// and choosing the top option adds a keyword that covers every matching item.
export default {
  name: "ClinicItemMultiSelect",
  props: {
    value: { type: Array, default: () => [] },
    // [{ key, name, code, isDrug }] from uniqueItemOptions()
    items: { type: Array, default: () => [] },
    label: { type: String, default: "รายการรักษา / ยาและสินค้า" },
    placeholder: {
      type: String,
      default: "ทุกรายการ · พิมพ์ชื่อหรือรหัสเพื่อค้นหา เช่น จัดฟัน, TD",
    },
  },
  computed: {
    itemByKey() {
      return new Map(this.items.map(i => [i.key, i]));
    },
    coveredKeys() {
      return selectedItemKeys(this.value, this.items);
    },
    // Items already covered by a pick or a keyword are left out of the list.
    options() {
      return this.items
        .filter(i => !this.coveredKeys.has(i.key))
        .map(i => ({ ...i, type: "item", _track: `i:${i.key}` }));
    },
    selectedOptions() {
      return this.value.map(s => {
        if (s.type === "keyword") return { type: "keyword", keyword: s.keyword, _track: `k:${s.keyword}` };
        // Fall back to the stored label when the item is not in the loaded rows.
        const item = this.itemByKey.get(s.key);
        return item
          ? { ...item, type: "item", _track: `i:${s.key}` }
          : { type: "item", key: s.key, name: s.label, code: "", isDrug: false, _track: `i:${s.key}` };
      });
    },
  },
  methods: {
    keywordCount(keyword) {
      return keywordMatches(keyword, this.items).length;
    },
    optionLabel(option) {
      if (option.type === "keyword") {
        return `${option.keyword}* · ${this.keywordCount(option.keyword)} รายการ`;
      }
      return itemLabel(option);
    },
    addKeyword(raw) {
      const keyword = (raw || "").trim();
      if (!keyword || !this.keywordCount(keyword)) return;
      if (this.value.some(s => s.type === "keyword" && s.keyword === keyword)) return;
      this.$emit("input", [...this.value, { type: "keyword", keyword }]);
    },
    onInput(opts) {
      this.$emit(
        "input",
        (opts || []).map(o =>
          o.type === "keyword"
            ? { type: "keyword", keyword: o.keyword }
            : { type: "item", key: o.key, label: itemLabel(o) }
        )
      );
    },
    clear() {
      this.$emit("input", []);
    },
  },
};
</script>

<style>
.clinic-multi-keyword-opt {
  font-weight: 600;
}
.clinic-multi-drug-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 4px;
  background: #e3f3e8;
  color: #1d6b3a;
  font-size: 10.5px;
}
/* Default tags are light grey; keyword and drug tags get light tints of their own. */
.multiselect__tag.clinic-multi-tag-keyword {
  background: #dbe8ff;
  color: #1a4fa8;
}
.multiselect__tag.clinic-multi-tag-drug {
  background: #e3f3e8;
  color: #1d6b3a;
}
</style>
